import { useState } from "react";
import { ErrorBoundary } from "@datadog/browser-rum-react";
import useMountEffect from "core/hooks/useMountEffect";

import Loader from "core/components/Loader";
import { ERROR_TYPES, REVERSE_ERROR_TYPES } from "core/constants";
import FailureErrorImage from "assets/failure.svg";
import { Row } from "core/styles";
import Button from "core/components/Button";
import Typography from "core/components/Typography";

const RootFallbackPage = ({ resetError, type, error }) => {
  const [showDebugButton, setShowDebugButton] = useState(false);
  const [showErrorTrace, setShowErrorTrace] = useState(false);

  const handleKeyDown = (event) => {
    // Ignore if user is typing in input/textarea/contenteditable
    const tag = event.target.tagName;
    const isTyping =
      tag === "INPUT" || tag === "TEXTAREA" || event.target.isContentEditable;

    if (isTyping) return;

    const cmdOrCtrl = event.metaKey || event.ctrlKey;

    if (cmdOrCtrl && event.shiftKey && event.key.toLowerCase() === "d") {
      event.preventDefault();
      setShowDebugButton((prev) => !prev);
    }
  };

  useMountEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <div
      data-test="failure-error-page"
      style={{
        height: "100%",
        width: "80%",
        margin: "0px auto",
        padding: "17vh 0px 15vh",
        display: "flex",
        alignItems: "center",
        flexDirection: "column",
        gap: "24px",
      }}
    >
      <img
        src={FailureErrorImage}
        alt="failure error page"
        style={{
          display: "block",
          width: "455px",
          margin: "0px auto",
        }}
      />
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <span
          style={{
            textAlign: "center",
            fontSize: "40px",
            fontWeight: "700",
            color: "var(--color-base-content)",
            maxWidth: "854px",
          }}
        >
          Something went wrong
        </span>

        <span
          style={{
            textAlign: "center",
            fontSize: "32px",
            fontWeight: "400",
            color: "var(--color-base-content)",
            maxWidth: "854px",
          }}
        >
          Well, that didn&apos;t go as planned. Please contact support if the
          issue persists.
        </span>
      </div>

      <Row>
        <Button
          onClick={() =>
            window.open(
              "https://success.populi.ai/hc/en-us",
              "_blank",
              "noopener,noreferrer"
            )
          }
        >
          Contact Support
        </Button>

        {showDebugButton && (
          <Button variant="outlined" onClick={() => setShowErrorTrace(true)}>
            Show Error Stack Trace
          </Button>
        )}
      </Row>

      <div
        style={{
          display: showErrorTrace ? "block" : "none",
          backgroundColor: "#fff",
          borderRadius: "5px",
          padding: "6px 12px",
        }}
      >
        <code>
          <Typography variant="h5" noMargin>
            Error Stack Trace
          </Typography>
          <div>
            Identified Error Type:{" "}
            {REVERSE_ERROR_TYPES[type] || "Unknown Error Type"}
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
            }}
          >
            {error?.stack || "No stack trace available"}
          </div>
        </code>
      </div>
    </div>
  );
};

const RootErrorBoundary = ({ children }) => {
  useMountEffect(() => {
    const cb = (e) => {
      console.debug("window.onError", e);
      if (
        e.message ===
        "ResizeObserver loop completed with undelivered notifications."
      ) {
        e.stopImmediatePropagation();
        e.preventDefault();
      }
    };

    window.addEventListener("error", cb);

    return () => {
      window.removeEventListener("error", cb);
    };
  });

  return (
    <ErrorBoundary
      fallback={({ error, resetError }) => {
        console.info("RootErrorBoundary", error);

        return (
          <RootFallbackPage
            resetError={resetError}
            type={ERROR_TYPES.ROOT_ERROR}
            error={error}
          />
        );
      }}
    >
      {children}
    </ErrorBoundary>
  );
};

export default RootErrorBoundary;
